import { ImageWithFallback } from '@/components/ImageWithFallback';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { productAPI } from '@/services/products';
import type {
  Product,
  ProductAttribute,
  ProductCategoryObject,
} from '@/types/products';
import { formatDate } from '@/utils/formatters';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Loader2, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteProductDialog } from './DeleteProductDialog';

interface GroupedAttributes {
  [key: string]: {
    values: string[];
    label: string;
  };
}

export function ProductDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [thumbPage, setThumbPage] = useState(0);

  const {
    data: productData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<Product, Error>({
    queryKey: ['product', id],
    queryFn: () => productAPI.getProductById(id as string) as Promise<Product>,
    enabled: !!id,
  });

  const product = productData;

  const { mutateAsync: deleteProduct, isPending: isDeleting } = useMutation({
    mutationFn: (pid: number | string) => productAPI.deleteProduct(pid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate('/products-management');
    },
  });

  const handleBack = () => navigate(-1);
  const handleEdit = () => navigate(`/products-management/${id}/edit`);
  const handleDelete = () => setDeleteOpen(true);

  const images = useMemo(() => {
    return (product?.images || []).map((img) => img.originalUrl);
  }, [product?.images]);

  const clampedActiveIndex = Math.min(
    Math.max(activeIndex, 0),
    Math.max(images.length - 1, 0)
  );

  const mainImage = images[clampedActiveIndex] || '';

  const otherImageIndexes = useMemo(() => {
    return Array.from(
      { length: Math.max(0, images.length) },
      (_, i) => i
    ).filter((i) => i !== clampedActiveIndex);
  }, [images, clampedActiveIndex]);

  const thumbsPerPage = 3;
  const totalThumbPages =
    Math.ceil(otherImageIndexes.length / thumbsPerPage) || 1;
  const currentThumbs = useMemo(() => {
    const start = thumbPage * thumbsPerPage;
    return otherImageIndexes.slice(start, start + thumbsPerPage);
  }, [thumbPage, otherImageIndexes, thumbsPerPage]);
  const remainingAfterCurrent = Math.max(
    0,
    otherImageIndexes.length - (thumbPage + 1) * thumbsPerPage
  );

  console.log(currentThumbs);

  const isStorageUrl = (url: string) => !url.startsWith('http');

  const getLocalizedName = (item: ProductCategoryObject) => {
    if (!item) return '';
    if (typeof item.name === 'string') return item.name;
    return item.name?.en || item.name?.uz || item.name?.ru || '';
  };

  const categoryName = getLocalizedName(
    product?.category as ProductCategoryObject
  );
  const subCategoryName = getLocalizedName(
    product?.subCategory as ProductCategoryObject
  );

  const groupAttributes = useCallback(
    (attributes: ProductAttribute[]): GroupedAttributes => {
      const grouped: GroupedAttributes = {};
      const sizeRelatedKeys = [
        'width',
        'height',
        'length',
        'thickness',
        'depth',
        'pile_height_inch',
      ];
      const colorKeys = ['color', 'colors'];

      const sizeGroup: string[] = [];
      const colorGroup: string[] = [];
      const otherGroups: { [key: string]: string[] } = {};

      attributes.forEach((attr) => {
        const { key, value } = attr;

        if (sizeRelatedKeys.includes(key.key)) {
          let unit = 'cm';
          if (key.key === 'pile_height_inch') {
            unit = 'inch';
          } else if (key.key === 'width' || key.key === 'height') {
            unit = 'ft';
          }
          sizeGroup.push(`${key.key.replace('_', ' ')}: ${value} ${unit}`);
        } else if (colorKeys.includes(key.key)) {
          if (!colorGroup.includes(value)) {
            colorGroup.push(value);
          }
        } else {
          if (!otherGroups[key.key]) {
            otherGroups[key.key] = [];
          }
          if (!otherGroups[key.key].includes(value)) {
            otherGroups[key.key].push(value);
          }
        }
      });

      if (sizeGroup.length > 0) {
        grouped['size'] = {
          values: sizeGroup,
          label: 'Dimensions',
        };
      }

      if (colorGroup.length > 0) {
        grouped['color'] = {
          values: colorGroup,
          label: 'Colors',
        };
      }
      Object.entries(otherGroups).forEach(([key, values]) => {
        grouped[key] = {
          values,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        };
      });

      return grouped;
    },
    []
  );

  const groupedAttributes = useMemo(() => {
    if (!product?.attributes?.length) return {};
    return groupAttributes(product.attributes);
  }, [product?.attributes, groupAttributes]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>Failed to load product</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{(error as Error)?.message || 'Something went wrong.'}</span>
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTitle>Product not found</AlertTitle>
          <AlertDescription>
            The product you are looking for does not exist or has been removed.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="py-3 ">
        <div className="flex flex-col-reverse sm:flex-row gap-4 md:items-center justify-between ">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4 md:mr-2" />
              <span className="hidden md:block">Back to Products</span>
            </Button>
            <div className="h-6 w-px bg-border" />
            <h1 className="text-xl md:!text-2xl font-semibold">
              {product.name}
            </h1>
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleEdit}>
              <Edit className="md:mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="md:mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>
        <DeleteProductDialog
          product={product}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onConfirm={async () => {
            if (!product?.id) return;
            await deleteProduct(product.id);
          }}
          isLoading={isDeleting}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="aspect-square w-full p-2 h-[400px] rounded-lg overflow-hidden border bg-white">
            <ImageWithFallback
              src={mainImage}
              alt={product.name}
              className="w-full h-full object-contain max-w-[500px] mx-auto"
              isStorageUrl={isStorageUrl(mainImage)}
            />
          </div>
          <div className="grid grid-cols-5 h-[100px]   gap-2">
            {currentThumbs.map((idx) => (
              <button
                key={idx}
                className={` rounded-lg border hover:ring-1 hover:ring-primary cursor-pointer overflow-hidden bg-white p-1 ${
                  idx === clampedActiveIndex ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setActiveIndex(idx)}
              >
                <ImageWithFallback
                  src={images[idx]}
                  alt={`${product.name} view ${idx + 1}`}
                  className="w-full h-full object-contain"
                  isStorageUrl={isStorageUrl(images[idx])}
                />
              </button>
            ))}
            <button
              className="rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-sm"
              onClick={() => setThumbPage((p) => (p + 1) % totalThumbPages)}
              disabled={otherImageIndexes.length <= thumbsPerPage}
              title={remainingAfterCurrent > 0 ? 'See more' : 'Loop'}
            >
              {otherImageIndexes.length > thumbsPerPage
                ? remainingAfterCurrent > 0
                  ? `+${remainingAfterCurrent} more`
                  : '>'
                : ''}
            </button>
          </div>
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center justify-between">
                  Basic Information
                  <Badge className="" variant="outline">
                    ID: {product.id}
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Category</p>
                  <div className="flex flex-wrap gap-2">
                    {product.subCategory?.category && (
                      <Badge variant="default" className="">
                        {getLocalizedName(product.subCategory.category)}
                      </Badge>
                    )}
                    {!product.subCategory?.category && categoryName && (
                      <Badge variant="default" className="">
                        {categoryName}
                      </Badge>
                    )}
                  </div>
                </div>
                {product.subCategory && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Subcategory
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="default" className="">
                        {subCategoryName}
                      </Badge>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">SKU</p>
                  <p className="font-mono text-sm">{product.sku || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="text-sm">
                    {product.createdAt ? formatDate(product.createdAt) : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Display Grouped Attributes */}
          {Object.entries(groupedAttributes).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Specifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(groupedAttributes).map(
                  ([key, { label, values }]) => (
                    <div key={key} className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        {label}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {values.map((value, idx) => (
                          <Badge
                            key={`${key}-${idx}`}
                            variant="outline"
                            className="text-sm"
                          >
                            {value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )
                )}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    Warranty
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-sm">
                      {product.warranty}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{product.description}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
